<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/24/solid'
const props = defineProps<{
    title: string
    message?: string
    success: boolean
    status?: 'success' | 'error' | 'info'
    timeout?: number
}>()
const model = defineModel<any>()

// Determine effective status from either status prop or legacy success prop
const effectiveStatus = computed(() => {
    if (props.status) return props.status
    return props.success ? 'success' : 'error'
})

// Tailwind classes based on status
const toastContainerClasses = computed(() => {
    switch (effectiveStatus.value) {
        case 'success':
            return 'bg-green-50 ring-green-200'
        case 'error':
            return 'bg-red-50 ring-red-200'
        case 'info':
        default:
            return 'bg-blue-50 ring-blue-200'
    }
})

const toastTitleClasses = computed(() => {
    switch (effectiveStatus.value) {
        case 'success':
            return 'text-green-800'
        case 'error':
            return 'text-red-800'
        case 'info':
        default:
            return 'text-blue-800'
    }
})

const toastMessageClasses = computed(() => {
    switch (effectiveStatus.value) {
        case 'success':
            return 'text-green-700'
        case 'error':
            return 'text-red-700'
        case 'info':
        default:
            return 'text-blue-700'
    }
})

const toastCloseButtonClasses = computed(() => {
    switch (effectiveStatus.value) {
        case 'success':
            return 'bg-green-50 text-green-500 hover:text-green-600 focus:ring-green-500'
        case 'error':
            return 'bg-red-50 text-red-500 hover:text-red-600 focus:ring-red-500'
        case 'info':
        default:
            return 'bg-blue-50 text-blue-500 hover:text-blue-600 focus:ring-blue-500'
    }
})

const timeOut = ref(false)
let timeoutId: number | null = null

watch(model, (newValue) => {
    if (newValue && !timeOut.value) {
        timeOut.value = true
        timeoutId = window.setTimeout(() => {
            console.log('closing notification')
            // Use nextTick to avoid reactive loops
            nextTick(() => {
                model.value = false
                timeOut.value = false
                timeoutId = null
            })
        }, props.timeout || 5000)
    } else if (!newValue && timeoutId) {
        // Clear timeout if notification is closed manually
        window.clearTimeout(timeoutId)
        timeoutId = null
        timeOut.value = false
    }
})
</script>

<template>
    <!-- Global notification live region, render this permanently at the end of the document -->
    <div
        aria-live="assertive"
        class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-big"
        style="z-index: 100000"
    >
        <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
            <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
            <transition
                enter-active-class="transform ease-out duration-300 transition"
                enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="model"
                    class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ring-opacity-5 z-big"
                    :class="toastContainerClasses"
                >
                    <div class="p-4">
                        <div class="flex items-start">
                            <div class="flex-shrink-0">
                                <CheckCircleIcon
                                    class="h-6 w-6 text-green-500"
                                    aria-hidden="true"
                                    v-if="effectiveStatus === 'success'"
                                />
                                <ExclamationCircleIcon
                                    class="h-6 w-6 text-red-500"
                                    aria-hidden="true"
                                    v-else-if="effectiveStatus === 'error'"
                                />
                                <InformationCircleIcon
                                    class="h-6 w-6 text-blue-500"
                                    aria-hidden="true"
                                    v-else
                                />
                            </div>
                            <div class="ml-3 w-0 flex-1 pt-0.5">
                                <p class="text-sm font-medium" :class="toastTitleClasses">{{ title }}</p>
                                <p class="mt-1 text-sm" :class="toastMessageClasses">
                                    {{ message }}
                                </p>
                            </div>
                            <div class="ml-4 flex flex-shrink-0">
                                <button
                                    type="button"
                                    @click="model = false"
                                    class="inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    :class="toastCloseButtonClasses"
                                >
                                    <span class="sr-only">Close</span>
                                    <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>
